import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  faChevronUp,
  faChevronDown,
  faBellExclamation,
} from '@fortawesome/pro-solid-svg-icons'
import {
  Alert,
  Badge,
  Card,
  CardColors,
  Icon,
  Image,
  Text,
  TimelineStepStatus,
  VisualTimeline,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import { CubkProgressPanel } from './CubkProgressPanel'
import { CubkCardState } from './lib/cubkProgress.types'
import {
  CUBK_ILLUSTRATIONS,
  CUBK_ILLUSTRATION_ALTS,
} from './cubkCard.constants'
import {
  BankingDisplayState,
  selectConnectBankPath,
} from '@/features/banking/banking.selectors'
import { useReselector } from '@/utils/sharedHooks'

type AlertConfig = {
  heading: string
  body: string
  ctaLabel: string
}

type BankingConfig = {
  subtitle: string
  pill: { color: 'gray' | 'orange'; label: string }
  alert: AlertConfig | null
  /** Whether the card is expanded by default. */
  defaultExpanded: boolean
}

export type ActiveBankingDisplayState = Exclude<
  BankingDisplayState,
  BankingDisplayState.Normal
>

const BANKING_CONFIG: Record<ActiveBankingDisplayState, BankingConfig> = {
  [BankingDisplayState.AwaitingApplication]: {
    subtitle: 'Your Heard Banking application is under review',
    pill: { color: 'gray', label: 'No action required' },
    alert: null,
    defaultExpanded: false,
  },
  [BankingDisplayState.ApprovedNoAccounts]: {
    subtitle: 'Complete Heard Banking setup',
    pill: { color: 'orange', label: 'Action required' },
    alert: {
      heading: 'Connect Heard bank account',
      body: "Now that you're approved, connect Heard banking to your bookkeeping.",
      ctaLabel: 'Connect',
    },
    defaultExpanded: true,
  },
  [BankingDisplayState.NoAccountsFallback]: {
    subtitle: 'Complete Heard Banking setup',
    pill: { color: 'orange', label: 'Action required' },
    alert: {
      heading: 'Connect bank account',
      body: 'Connect your bank so Heard can automatically sync your transactions and start your bookkeeping.',
      ctaLabel: 'Connect',
    },
    defaultExpanded: true,
  },
}

const TIMELINE_LABELS = [
  'Bank transactions import',
  'Auto-categorization of transactions',
  'Bookkeeping review',
  'Books complete',
]

const upcomingSteps = TIMELINE_LABELS.map((label) => ({
  label,
  status: TimelineStepStatus.NotStarted,
}))

interface Props {
  displayState: ActiveBankingDisplayState
}

export const CubkCardBanking = ({ displayState }: Props) => {
  const navigate = useNavigate()
  const config = BANKING_CONFIG[displayState]
  const connectBankPath = useReselector(selectConnectBankPath)
  const [isExpanded, setIsExpanded] = useState(config.defaultExpanded)

  const handleCtaClick = () => {
    if (config.alert) navigate(connectBankPath)
  }

  return (
    <Card color={CardColors.BLUE_GRADIENT} data-testid="cubk-card-banking">
      <div className="flex flex-col gap-4">
        <div className="lg:hidden">
          <Badge color={config.pill.color} variant="secondary">
            {config.pill.label}
          </Badge>
        </div>

        <div className="flex items-center gap-4">
          <Image
            src={CUBK_ILLUSTRATIONS[CubkCardState.BANK_INCOMPATIBLE]}
            alt={CUBK_ILLUSTRATION_ALTS[CubkCardState.BANK_INCOMPATIBLE]}
            width={80}
            height={80}
            objectFit="contain"
            className="hidden lg:block"
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-col">
              <Text variant={TypographyStyles.H1}>
                Account connection needed to start bookkeeping - Banking
              </Text>
              <p>
                <span className="label-sm text-neutral-500">Status:</span>{' '}
                <span className="body-sm text-neutral-500">
                  {config.subtitle}
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="hidden lg:flex">
              <Badge color={config.pill.color} variant="secondary">
                {config.pill.label}
              </Badge>
            </div>
            <button
              onClick={() => setIsExpanded((v) => !v)}
              className="flex-shrink-0 p-2"
              aria-label={isExpanded ? 'Collapse card' : 'Expand card'}
              data-testid="cubk-card-banking-toggle"
            >
              <Icon
                icon={isExpanded ? faChevronUp : faChevronDown}
                className="text-neutral-500 shrink-0"
              />
            </button>
          </div>
        </div>
      </div>

      <div
        aria-hidden={!isExpanded}
        data-testid="cubk-card-banking-expanded-wrapper"
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">
          <div className="mt-8 flex flex-col gap-8">
            {config.alert && (
              <Alert
                variant="priority"
                customIconName={faBellExclamation}
                title={`Action required: ${config.alert.heading}`}
                description={config.alert.body}
                ctaText={config.alert.ctaLabel}
                ctaOnClick={handleCtaClick}
              />
            )}
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="w-full lg:w-1/2">
                <VisualTimeline steps={upcomingSteps} />
              </div>
              <div className="w-full lg:w-1/2">
                <CubkProgressPanel />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  )
}
